import React from 'react'
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useIntl } from 'react-intl'
import { i18nMessages } from '../../Logic/CodeAccessible.i18n.Messages'
import { CapeeshColors } from '../../assets/ColorPalette'

const useStyles = makeStyles(() => ({
    bar: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '20px',
    },
    control: {
        minWidth: '220px',
    },
    note: {
        marginTop: '8px',
        color: CapeeshColors.Gray1,
        fontSize: '13px',
    },
}))

export const CourseStatsDateRange = {
    All: 'all',
    Last30: 'last30',
    Last90: 'last90',
    LastYear: 'lastYear',
    Custom: 'custom',
}

/**
 * Turns a named range into the from/to the API expects. Kept next to the picker so the labels and
 * the dates cannot drift apart.
 *
 * customFrom and customTo are yyyy-MM-dd strings from date inputs, and only used for the Custom
 * range. Either may be left blank, which means open ended on that side.
 */
export const resolveDateRange = (range, customFrom, customTo) => {
    if (range === CourseStatsDateRange.Custom) {
        return {
            fromDate: customFrom ? new Date(customFrom).toISOString() : undefined,
            // End of the chosen day, not midnight at the start of it. Picking the 12th as the
            // to-date should include everything that happened on the 12th - the endpoint filters
            // on CreatedAt < toDate, so midnight would silently drop the whole final day.
            toDate: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : undefined,
        }
    }

    if (range === CourseStatsDateRange.All) {
        return { fromDate: undefined, toDate: undefined }
    }

    const now = new Date()
    const from = new Date()

    if (range === CourseStatsDateRange.Last30) {
        from.setDate(from.getDate() - 30)
    } else if (range === CourseStatsDateRange.Last90) {
        from.setDate(from.getDate() - 90)
    } else {
        from.setFullYear(from.getFullYear() - 1)
    }

    return { fromDate: from.toISOString(), toDate: now.toISOString() }
}

/** True when a custom range is set the wrong way round. */
export const isCustomRangeInvalid = (range, customFrom, customTo) =>
    range === CourseStatsDateRange.Custom && !!customFrom && !!customTo && customFrom > customTo

const CourseStatsScopeBar = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    return (
        <div>
            <div className={classes.bar}>
                <FormControl className={classes.control}>
                    <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsColumnCourse)}</InputLabel>
                    <Select
                        value={props.selectedCourseGroupId || 'all'}
                        onChange={(event) =>
                            props.onCourseGroupChange(event.target.value === 'all' ? null : event.target.value)
                        }>
                        <MenuItem value="all">
                            {intl.formatMessage(i18nMessages.courseStatisticsAllCourses)}
                        </MenuItem>
                        {(props.courseGroups || []).map((courseGroup) => (
                            <MenuItem key={courseGroup.courseGroupId} value={courseGroup.courseGroupId}>
                                {courseGroup.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.control}>
                    <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsPeriod)}</InputLabel>
                    <Select value={props.dateRange} onChange={(event) => props.onDateRangeChange(event.target.value)}>
                        <MenuItem value={CourseStatsDateRange.All}>
                            {intl.formatMessage(i18nMessages.courseStatisticsDateRangeAll)}
                        </MenuItem>
                        <MenuItem value={CourseStatsDateRange.Last30}>
                            {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLast30)}
                        </MenuItem>
                        <MenuItem value={CourseStatsDateRange.Last90}>
                            {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLast90)}
                        </MenuItem>
                        <MenuItem value={CourseStatsDateRange.LastYear}>
                            {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLastYear)}
                        </MenuItem>
                    </Select>
                </FormControl>
            </div>

            {props.dateRange !== CourseStatsDateRange.All && (
                <Typography className={classes.note}>
                    {intl.formatMessage(i18nMessages.courseStatisticsCompletionNoteOnDateRange)}
                </Typography>
            )}
        </div>
    )
}

export default CourseStatsScopeBar
